import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { FormikProps } from 'formik';
import { LatLng } from 'leaflet';
import { isEqual } from 'lodash';

import {
  Box,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
  Autocomplete,
  Checkbox,
  ListItemText,
  IconButton,
  Tooltip,
  TooltipProps,
  tooltipClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';

// Services
import { getStations } from '../../../services/station.service';
import { getCarriers } from '../../../services/facility.service';

// Utils
import { calculateCentroid } from '../../../utils/mapHelper';

// Hooks
import { useAppSelector } from '../../../redux/hook';

// Types
import { FilterableTypes } from '../../../types/FilterableTypes';
import { getCoordinatesPreset } from '../../../constants/Constants';
import { Coordinates } from '../../../types/Coordinates';
import { Station } from '../../../types/Station';
import { Geofence, GeofenceLevel, GeofenceType } from '../../../types/Geofence';
import { AddCarrierPayload, Carrier, CarrierOption } from '../../../types/Carrier';

// Components
import Map from '../../maps/Map';
import { InfoOutlined } from '@mui/icons-material';

const PREFIX = 'facilityForm';

const classes = {
  root: `${PREFIX}-root`,
  pointerCursor: `${PREFIX}-pointerCursor`,
  dropdown: `${PREFIX}-dropdown`,
  name: `${PREFIX}-name`,
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.root}`]: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
    },
  },

  [`& .${classes.pointerCursor}`]: {
    cursor: 'pointer',
  },

  [`& .${classes.dropdown}`]: {
    width: 200,
    display: 'flex',
    justifyContent: 'left',
  },

  [`& .${classes.name}`]: {
    width: 200,
  },
}));

// AMS as default location
const DEFAULT_LOCATION = {
  type: 'Point',
  latitude: 52.3086013794,
  longitude: 4.763889789579999,
};

export interface FacilityInput {
  id: string;
  name: string;
  stationIataCode: string;
  stationId?: string;
  facilityType?: number;
  latitude: string;
  longitude: string;
  geofences?: Geofence[];
  carriers?: string[];
}

interface FacilityProps {
  formRef: FormikProps<FacilityInput>;
  handleCarrierPayload?: (carriers: AddCarrierPayload) => void;
  defaultCarriers?: any;
}

interface CoordinatesWithId extends Coordinates {
  id?: string;
}

export default function AddFacility(props: FacilityProps) {
  const settingState = useAppSelector((state) => state.settings);
  const facilityState = useAppSelector((state) => state.facilities);
  const geofenceState = useAppSelector((state) => state.geofences);
  const mapState = useAppSelector((state) => state.maps);

  const [centerMap, setCenterMap] = useState<LatLng>();
  const [positions, setPositions] = useState<CoordinatesWithId[]>([]);
  const [options, setOptions] = useState<readonly Station[]>([]);
  const [carrierOptions, setCarrierOptions] = useState<Carrier[]>([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [selectedCarriers, setSelectedCarriers] = useState<CarrierOption[]>([]);

  const [inputValue, setInputValue] = useState<string>('');
  const [hydratedSelectedOptions, setHydratedSelectedOptions] = useState<Record<string, CarrierOption>>({});

  const [isReadonly, setIsReadonly] = useState<boolean>(false);

  const fetchCarriers = async (value?: string) => {
    setLoadingCarriers(true);
    try {
      const res = await getCarriers({
        skip: 0,
        take: 50,
        filters: value ? [{ carrierCode: value, filterType: FilterableTypes.Contains }] : null,
      } as any);
      if (res.data?.results) setCarrierOptions(res.data.results);
    } finally {
      setLoadingCarriers(false);
    }
  };

  const debouncedFetchCarriers = debounce(async (val: string) => {
    await fetchCarriers(val);
  }, 600);

  const mappedCarrierOptions: CarrierOption[] = useMemo(
    () =>
      carrierOptions.map((c) => ({
        id: c.id,
        carrierCode: c.carrierCode ?? '',
        iataCode: c.iataCode ?? '',
        label: c.carrierCode ?? '',
        islocked: c.isLocked ?? false,
      })),
    [carrierOptions]
  );
  const mergedOptions: CarrierOption[] = useMemo(() => {
    const fromApi = mappedCarrierOptions;
    const extras = Object.values(hydratedSelectedOptions).filter((opt) => !fromApi.some((a) => a.id === opt.id));
    return [...fromApi, ...extras];
  }, [mappedCarrierOptions, hydratedSelectedOptions]);

  const currentIds = selectedCarriers.map((c) => c.id);
  const valueForAutocomplete: CarrierOption[] = useMemo(() => {
    return mergedOptions.filter((opt) => currentIds.includes(opt.id));
  }, [mergedOptions, currentIds]);

  useEffect(() => {
    fetchCarriers();
  }, []);

  useEffect(() => {
    const missing = currentIds.filter((id) => !mergedOptions.some((o) => o.id === id));
    if (missing.length === 0) return;

    const placeholders: Record<string, CarrierOption> = {};
    missing.forEach((id) => {
      placeholders[id] = { id, carrierCode: '', iataCode: '', label: '', isLocked: incomingLockFor(id) ?? false };
    });

    setHydratedSelectedOptions((prev) => ({ ...prev, ...placeholders }));
  }, [currentIds, mergedOptions]);

  const prevDefaultRef = useRef<any>(null);

  useEffect(() => {
    const carrierIds = props.defaultCarriers?.carrierIds;
    if (!carrierIds || carrierIds.length === 0) return;

    const formHasCarriers = Array.isArray(props.formRef.values.carriers) && props.formRef.values.carriers.length > 0;
    if (formHasCarriers) return;

    const initialSelected: CarrierOption[] = carrierIds.map((c: any) => {
      const match = mergedOptions.find((m) => m.id === c.objectId);
      return match ?? { id: c.objectId, carrierCode: '', iataCode: '', label: '', isLocked: c.isLocked ?? false };
    });

    if (!isEqual(initialSelected, selectedCarriers)) {
      setSelectedCarriers(initialSelected);
    }

    const hydrated: Record<string, CarrierOption> = {};
    initialSelected.forEach((c) => (hydrated[c.id] = c));

    // only merge hydratedSelectedOptions if different
    if (!isEqual(hydrated, hydratedSelectedOptions)) {
      setHydratedSelectedOptions((prev) => ({ ...prev, ...hydrated }));
    }

    const newPayload: AddCarrierPayload = {
      carrierIds: initialSelected.map((c) => {
        const incoming = props.defaultCarriers?.carrierIds?.find((d: any) => d.objectId === c.id);
        return { objectId: c.id, isLocked: incoming ? incoming.isLocked : true };
      }),
    };

    if (!isEqual(props.defaultCarriers, newPayload)) {
      props.handleCarrierPayload?.(newPayload);
    }

    prevDefaultRef.current = props.defaultCarriers;
  }, [props.defaultCarriers, mergedOptions, props.formRef.values.carriers]);

  // Helper to get incoming lock
  const incomingLockFor = (id: string) => props.defaultCarriers?.carrierIds?.find((d: any) => d.objectId === id)?.isLocked;

  // Remove carrier safely
  const handleRemoveCarrier = (id: string) => {
    const newSelected = selectedCarriers.filter((c) => c.id !== id);

    // Remove carrier from hydrated options
    setHydratedSelectedOptions((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });

    // Use selected carrier's isLocked value
    const newPayload: AddCarrierPayload = {
      carrierIds: newSelected.map((c) => ({
        objectId: c.id,
        isLocked: c.isLocked ?? incomingLockFor(c.id) ?? true,
      })),
    };

    setSelectedCarriers(newSelected);
    props.handleCarrierPayload?.(newPayload);
  };

  useEffect(() => {
    if (props.formRef.values) {
      const { geofences, id, name } = props.formRef.values;

      let latitude = props.formRef.values.latitude ?? '';
      let longitude = props.formRef.values.longitude ?? '';

      // If both lat & lng are missing or invalid, set defaults to the default point
      const latMissing = latitude === undefined || latitude === '' || isNaN(Number(latitude));
      const lngMissing = longitude === undefined || longitude === '' || isNaN(Number(longitude));

      if (latMissing && lngMissing) {
        const latStr = DEFAULT_LOCATION.latitude.toString();
        const lngStr = DEFAULT_LOCATION.longitude.toString();

        // update Formik values (strings)
        props.formRef.setFieldValue('latitude', latStr);
        props.formRef.setFieldValue('longitude', lngStr);

        latitude = latStr;
        longitude = lngStr;
      }

      const latNum = isNaN(Number(latitude)) ? DEFAULT_LOCATION.latitude : parseFloat(latitude);
      const lngNum = isNaN(Number(longitude)) ? DEFAULT_LOCATION.longitude : parseFloat(longitude);

      const facilityLocation = new LatLng(latNum, lngNum);

      if (geofences && geofences.length > 0) setIsReadonly(true);
      else setIsReadonly(false);

      if (!centerMap) {
        if (geofences && geofences?.length > 0) {
          const coordinates = geofences[0].coordinates;
          if (coordinates && coordinates.length > 0) {
            // calculate Centroid from coordinates
            const centroid = calculateCentroid(coordinates);
            if (centroid) setCenterMap(centroid);
          } else {
            setCenterMap(facilityLocation);
          }
        } else {
          setCenterMap(facilityLocation);
        }

        setPositions([
          {
            _type: 'Facility',
            latLng: new LatLng(parseFloat(latitude), parseFloat(longitude)),
            color: 'green',
            fillColor: 'green',
            showMarker: false,
            showCircleMarker: false,
            fillOpacity: 1.0,
            circleRadius: 5,
          },
        ]);

        if (geofences && geofences.length > 0) {
          const selectGeofence = geofences[0];
          if (selectGeofence) {
            const { stationIataCode, coordinates } = selectGeofence;
            setPositions((prevState) => [
              ...prevState,
              {
                ...getCoordinatesPreset('Facility'),
                latLng: new LatLng(0, 0),
                geofences: [
                  {
                    id: '0',
                    type: GeofenceType.polygon,
                    level: GeofenceLevel.facility,
                    name: selectGeofence.name,
                    facilityName: name,
                    stationIataCode,
                    coordinates,
                    showMarker: true,
                  },
                ],
                id,
                editable: true,
                showPolygon: true,
              },
            ]);
          }
        }

        if (geofenceState.stationGeofences && geofenceState.stationGeofences.length > 0) {
          const airportGeofeces = geofenceState.stationGeofences.map((geofence) => {
            return {
              ...getCoordinatesPreset('AirportGeofence'),
              geofences: [geofence],
              showPolygon: true,
            } as Coordinates;
          });

          if (airportGeofeces && airportGeofeces.length > 0) {
            setPositions((prevPositions) => {
              let currentPositions = [...prevPositions];
              currentPositions = currentPositions.filter((v) => v._type !== 'AirportGeofence');
              currentPositions = [...currentPositions, ...airportGeofeces];
              return currentPositions;
            });
          }
        }

        if (facilityState.facilities && facilityState.facilities.length > 0) {
          const geofenceList = facilityState.facilities
            .filter((v) => v.geofences && v.geofences.length > 0 && v.id !== id)
            ?.map((fa) => {
              if (fa.geofences && fa.geofences.length > 0) {
                const selectGeofence = fa?.geofences[0];
                const { stationIataCode, coordinates } = selectGeofence;
                return {
                  geofences: [
                    {
                      name: selectGeofence.name,
                      facilityName: fa.name,
                      stationIataCode,
                      coordinates,
                    },
                  ],
                  id: fa.id,
                  color: '#5D3FD3',
                  showPolygon: true,
                } as any;
              }
            });
          if (geofenceList && geofenceList.length > 0) {
            setPositions((prevState) => [...prevState, ...geofenceList]);
          }
        }
      }

      fetchStations();
    }
  }, [props.formRef.values]);

  useEffect(() => {
    if (mapState?.targetLocation) {
      props.formRef.setFieldValue('latitude', mapState.targetLocation.latitude);
      props.formRef.setFieldValue('longitude', mapState.targetLocation.longitude);
    }
  }, [mapState?.targetLocation]);

  const renderGeofences = () => {
    const geofences = props.formRef?.values?.geofences;
    if (geofences && geofences.length > 0) {
      const coordinates = geofences[0].coordinates;
      return (
        <div>
          <ul>
            {coordinates &&
              coordinates?.map((v, index) => {
                return <li key={index}>{`[${v[0]}, ${v[1]} ]`}</li>;
              })}
          </ul>
        </div>
      );
    } else {
      return <span style={{ marginLeft: 10 }}> No geofence set</span>;
    }
  };

  const handleCreatePolygon = async (e) => {
    const { layerType, layer } = e;
    if (layerType === 'polygon') {
      const latlngs = layer.getLatLngs()[0];
      if (props.formRef.values.id && props.formRef.values.geofences && props.formRef.values.geofences.length > 0) {
        const geofences = props.formRef.values.geofences ?? [];
        const payload = geofences.map((geofence) => {
          return {
            ...geofence,
            coordinates: latlngs.map((f: LatLng) => [f.lng, f.lat]),
          };
        });
        props.formRef.setFieldValue('geofences', payload);
      } else {
        //create new
        props.formRef.setFieldValue('geofences', [
          {
            coordinates: latlngs.map((f: LatLng) => [f.lng, f.lat]),
          },
        ]);
      }
    }
  };

  const handleEditPolygon = async (e) => {
    const {
      layers: { _layers },
    } = e;

    Object.values(_layers).map(({ editing }: any) => {
      const latlngs = editing.latlngs[0][0];
      const geofences = props.formRef.values.geofences ?? [];
      const payload = geofences.map((geofence) => {
        return {
          ...geofence,
          coordinates: latlngs.map((f) => [f.lng, f.lat]),
        };
      });

      props.formRef.setFieldValue('geofences', payload);

      if (positions) {
        setPositions(
          positions.map((x) => {
            if (x.id === props.formRef.values.id) {
              if (x.showPolygon && x.geofences) {
                x.geofences[0].coordinates = latlngs.map((f) => [f.lng, f.lat]);
              }
            }

            return x;
          })
        );
      }
    });
  };

  const handleDeletePolygon = async () => {
    const geofences = props.formRef.values.geofences ?? [];
    if (geofences && geofences.length > 0) {
      const geofence = geofences[0];
      props.formRef.setFieldValue('geofences', [
        {
          ...geofence,
          coordinates: [], //reset geofence
        },
      ]);
    }
  };

  const debouncedFetchOptions = debounce(async (value) => {
    await fetchStations(value);
  }, 700);

  const fetchStations = async (value?: string) => {
    await getStations({
      skip: 0,
      take: 100,
      filters: value ? [{ iataCode: value, filterType: FilterableTypes.Contains }] : null,
    }).then((res) => {
      if (res.data?.results) {
        setOptions(res.data.results);
      }
    });
  };

  const selectedStationOption = useMemo(() => {
    const stationCode = props.formRef.values?.stationIataCode;
    if (!stationCode) return null;

    // look for the station inside options (by iataCode)
    const found = options.map((p: Station) => ({ label: p.iataCode, id: p.id })).find((o) => o.label === stationCode);
    // if not found, return a fallback object so Autocomplete can display the typed value
    return found ?? { label: stationCode, id: stationCode };
  }, [options, props.formRef.values?.stationIataCode]);

  return (
    <StyledPaper elevation={3} style={{ overflowX: 'auto' }}>
      <form onSubmit={props.formRef.handleSubmit} style={{ width: '100%', height: 700 }}>
        <Grid container spacing={3}>
          <Grid xs={12} sm={12} lg={7} item>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              {centerMap && positions && (
                <Map
                  center={centerMap}
                  onCreated={handleCreatePolygon}
                  onEdited={handleEditPolygon}
                  onDeleted={handleDeletePolygon}
                  coordinates={positions}
                  isEditingEnabled={true}
                  showToggleButtons={false}
                  height='675px'
                  showSatelliteMap={true}
                />
              )}
            </Box>
          </Grid>
          <Grid xs={12} sm={12} lg={5} item className={classes.root}>
            <Grid xs={12} item>
              <TextField
                id='name'
                name='name'
                label='Name'
                value={props.formRef.values?.name ?? ''}
                onChange={props.formRef.handleChange}
                error={props.formRef.touched.name && Boolean(props.formRef.errors.name)}
                helperText={props.formRef.touched.name && props.formRef.errors.name}
                placeholder='Enter Name'
                variant='standard'
                size='small'
                fullWidth
                required
                margin='dense'
                style={{ width: 400 }}
              />
            </Grid>

            <Grid xs={12} item className={classes.root}>
              <Autocomplete
                disablePortal
                options={options.map((p: Station) => ({ label: p.iataCode, id: p.id }))}
                value={selectedStationOption}
                onInputChange={(event, value, reason) => {
                  if (reason === 'input' && value) {
                    debouncedFetchOptions(value);
                  }
                }}
                onChange={(event: any, value: any | null) => {
                  if (!value) {
                    // cleared or no selection
                    props.formRef.setFieldValue('stationIataCode', '');
                    props.formRef.setFieldValue('stationId', '');
                    // reset lat/lng to default or blanks as you prefer:
                    props.formRef.setFieldValue('latitude', DEFAULT_LOCATION.latitude.toString());
                    props.formRef.setFieldValue('longitude', DEFAULT_LOCATION.longitude.toString());
                    setCenterMap(new LatLng(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude));
                    return;
                  }

                  // value is an object that came from the API options
                  const selectedStation = options.find((s) => s.id === value.id);
                  if (selectedStation) {
                    props.formRef.setFieldValue('latitude', selectedStation.location?.latitude ?? DEFAULT_LOCATION.latitude);
                    props.formRef.setFieldValue('longitude', selectedStation.location?.longitude ?? DEFAULT_LOCATION.longitude);
                    props.formRef.setFieldValue('stationIataCode', selectedStation.iataCode);
                    props.formRef.setFieldValue('stationId', selectedStation.id);
                    setCenterMap(
                      new LatLng(
                        selectedStation.location?.latitude ?? DEFAULT_LOCATION.latitude,
                        selectedStation.location?.longitude ?? DEFAULT_LOCATION.longitude
                      )
                    );
                  } else {
                    // should not happen because options come from API, but fallback to safe defaults
                    props.formRef.setFieldValue('stationIataCode', '');
                    props.formRef.setFieldValue('stationId', '');
                    props.formRef.setFieldValue('latitude', DEFAULT_LOCATION.latitude.toString());
                    props.formRef.setFieldValue('longitude', DEFAULT_LOCATION.longitude.toString());
                    setCenterMap(new LatLng(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude));
                  }
                }}
                sx={{ width: 100 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id='stationIataCode'
                    placeholder='Station'
                    variant='standard'
                    size='small'
                    fullWidth
                    required
                    style={{ width: 110 }}
                    error={props.formRef.touched.stationIataCode && Boolean(props.formRef.errors.stationIataCode)}
                    helperText={props.formRef.touched.stationIataCode && props.formRef.errors.stationIataCode}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} item className={classes.root}>
              <Select
                variant='outlined'
                id='facilityType'
                name='facilityType'
                value={props.formRef.values?.facilityType}
                onChange={(e) => props.formRef.setFieldValue('facilityType', e.target.value ?? 0)}
                onBlur={props.formRef.handleBlur}
                error={props.formRef.touched?.facilityType && Boolean(props.formRef.errors?.facilityType)}
                size='small'
                className={classes.dropdown}
              >
                {settingState.facilityTypes?.value.map((val, index: number) => {
                  return (
                    <MenuItem key={index} value={val.text}>
                      {val.value}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText error={props.formRef.touched?.facilityType && Boolean(props.formRef.errors?.facilityType)}>
                {props.formRef.touched?.facilityType && props.formRef.errors?.facilityType}
              </FormHelperText>
            </Grid>

            <Grid xs={12} item className={classes.root} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Autocomplete
                multiple
                disablePortal
                disableCloseOnSelect
                filterSelectedOptions
                id='carrier-codes-autocomplete'
                options={mergedOptions}
                loading={loadingCarriers}
                sx={{ width: 280 }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                  const upper = (newInputValue ?? '').toUpperCase();
                  if (reason === 'input') {
                    setInputValue(upper);
                    debouncedFetchCarriers(upper);
                  } else if (reason === 'clear') {
                    setInputValue('');
                  }
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.carrierCode ?? option?.label ?? '')}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof value === 'string') return option.carrierCode === value || option.id === value;
                  return option.id === value.id;
                }}
                value={valueForAutocomplete}
                renderOption={(propsRender, option, { selected }) => (
                  <li {...propsRender}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} size='small' />
                    <ListItemText primary={option.carrierCode ?? option.label ?? ''} />
                  </li>
                )}
                onChange={(event, newValue) => {
                  const newVals = (newValue || []) as CarrierOption[];
                  const newIds = newVals.map((v) => v.id);

                  const newHydratedFromValue: Record<string, CarrierOption> = {};
                  newVals.forEach((v: CarrierOption) => {
                    newHydratedFromValue[v.id] = {
                      ...v,
                      isLocked: hydratedSelectedOptions[v.id]?.isLocked ?? incomingLockFor(v.id) ?? true,
                    };
                  });

                  // merge hydrated entries
                  setHydratedSelectedOptions((prev) => ({ ...prev, ...newHydratedFromValue }));

                  setSelectedCarriers(newVals);

                  // notify parent comp
                  props.handleCarrierPayload?.({
                    carrierIds: newIds.map((id) => ({
                      objectId: id,
                      isLocked: incomingLockFor(id) !== undefined ? incomingLockFor(id) : true,
                    })),
                  });

                  setInputValue('');
                }}
                renderInput={(params) => (
                  <TextField {...params} variant='standard' label='Carriers (code)' size='small' placeholder='Type carrier code...' />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    const locked = hydratedSelectedOptions[option.id]?.isLocked ?? incomingLockFor(option.id) ?? true;

                    const bgColor = locked ? 'darkgray' : 'orange';

                    return (
                      <span
                        key={option.id}
                        {...rest}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: 14,
                          backgroundColor: bgColor,
                          color: '#fff',
                          marginRight: 6,
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ fontSize: 13 }}>{option.carrierCode ?? ''}</span>

                        <IconButton
                          size='small'
                          aria-label={`remove ${option.carrierCode ?? ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveCarrier(option.id);
                          }}
                          style={{ padding: 4, color: 'inherit' }}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </span>
                    );
                  })
                }
              />
              <LightTooltip
                style={{ marginLeft: '0.5em', marginTop: '1em' }}
                title={
                  <>
                    <div>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'orange',
                          marginRight: 6,
                        }}
                      />
                      <Typography variant='caption' style={{ color: 'orange', backgroundColor: 'white' }}>
                        System Generated
                      </Typography>
                    </div>
                    <div>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'darkgrey',
                          marginRight: 6,
                        }}
                      />
                      <Typography variant='caption' style={{ color: 'darkgrey', backgroundColor: 'white' }}>
                        Manually Updated
                      </Typography>
                    </div>
                  </>
                }
              >
                <InfoOutlined />
              </LightTooltip>
            </Grid>

            <Grid xs={6} sm={6} item className={classes.root}>
              <TextField
                id='latitude'
                name='latitude'
                label='Latitude'
                disabled={isReadonly}
                value={props.formRef.values?.latitude ?? ''}
                onChange={props.formRef.handleChange}
                error={props.formRef.touched.latitude && Boolean(props.formRef.errors.latitude)}
                helperText={props.formRef.touched.latitude && props.formRef.errors.latitude}
                placeholder='Enter Latitude'
                variant='standard'
                size='small'
                style={{ width: 130 }}
                required
                margin='dense'
              />
            </Grid>
            <Grid xs={6} sm={6} item className={classes.root}>
              <TextField
                id='longitude'
                name='longitude'
                label='Longitude'
                disabled={isReadonly}
                value={props.formRef.values?.longitude ?? ''}
                onChange={props.formRef.handleChange}
                error={props.formRef.touched.longitude && Boolean(props.formRef.errors.longitude)}
                helperText={props.formRef.touched.longitude && props.formRef.errors.longitude}
                margin='dense'
                placeholder='Enter Longitude'
                style={{ width: 130 }}
                variant='standard'
                size='small'
                required
              />
            </Grid>
            <Grid xs={12} item className={classes.root}>
              <Typography variant='subtitle1' gutterBottom style={{ marginTop: 10 }}>
                Geofence
              </Typography>
              <div style={{ fontSize: 'small' }}>{renderGeofences()}</div>
            </Grid>
            <Grid xs={12} item className={classes.root}>
              <Typography variant='subtitle1' gutterBottom style={{ marginTop: 15 }}>
                Facility Owner
              </Typography>
              <Typography variant='body2' gutterBottom style={{ paddingLeft: 10 }}>
                Facility owner not set
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </StyledPaper>
  );
}
